/**
 * Workflow Field Permissions Configuration
 *
 * This file serves as the single source of truth for all workflow status
 * field permissions across all user roles and invoice classifications.
 *
 * All configurations are based on validated business requirements and
 * provide granular control over field visibility and editability.
 */

import { InvoiceStatus } from '@config/invoice-config';
import { UserRole } from '@config/workflow-config';

// UI-specific fields (excludes system fields like STATUS, ASSIGNED_TO, PREVIOUS_ASSIGNEE)
export enum WorkflowUIField {
  CLASSIFICATION = 'classification',
  NEW_STOCK = 'new_stock',
  PRODUCT_ADDED = 'product_added',
  SUPPLIER = 'supplier',
  INVOICE_NUMBER = 'invoice_number',
  INVOICE_DATE = 'invoice_date',
  DUE_DATE = 'due_date',
  TOTAL_AMOUNT = 'total_amount',
  SUPPLIER_ABN = 'supplier_abn',
  STORE_ID = 'store_id',
  PO = 'po',
  SO = 'so',
  IR = 'ir',
  INV = 'inv',
  CLAIM = 'claim',
  CN = 'cn',
  CREDIT_AMOUNT = 'credit_amount',
  NOTES = 'notes'
}

// Field permission interface
export interface FieldPermission {
  visible: boolean;
  editable: boolean;
}

// Role permissions for a specific status
export interface RolePermissions {
  [UserRole.ADMINISTRATOR]: Record<WorkflowUIField, FieldPermission>;
  [UserRole.FINANCE]: Record<WorkflowUIField, FieldPermission>;
  [UserRole.STOCK]: Record<WorkflowUIField, FieldPermission>;
}

// Complete workflow configuration
export interface WorkflowConfiguration {
  [InvoiceStatus.UNPROCESSED]: RolePermissions;
  [InvoiceStatus.ADDING]: RolePermissions;
  [InvoiceStatus.NEW_PRODUCTS]: RolePermissions;
  [InvoiceStatus.IR]: RolePermissions;
  [InvoiceStatus.INVOICING]: RolePermissions;
  [InvoiceStatus.HEAD_OFFICE_STOCK]: RolePermissions;
  [InvoiceStatus.EXPENSE_INVOICES]: RolePermissions;
  [InvoiceStatus.ENTERING_STOCK]: RolePermissions;
  [InvoiceStatus.ENTERING_EXPENSE]: RolePermissions;
  [InvoiceStatus.CREDIT_NOTE]: RolePermissions;
  [InvoiceStatus.CREDIT_ENTERING]: RolePermissions;
  [InvoiceStatus.COMPLETED]: RolePermissions;
}

// Helper function to create field permissions
const createFieldPermissions = (
  adminPermissions: Partial<Record<WorkflowUIField, FieldPermission>>,
  financePermissions: Partial<Record<WorkflowUIField, FieldPermission>>,
  stockPermissions: Partial<Record<WorkflowUIField, FieldPermission>>
): RolePermissions => {
  // Default permissions for all fields
  const defaultPermissions: Record<WorkflowUIField, FieldPermission> = {
    [WorkflowUIField.CLASSIFICATION]: { visible: false, editable: false },
    [WorkflowUIField.NEW_STOCK]: { visible: false, editable: false },
    [WorkflowUIField.PRODUCT_ADDED]: { visible: false, editable: false },
    [WorkflowUIField.SUPPLIER]: { visible: false, editable: false },
    [WorkflowUIField.INVOICE_NUMBER]: { visible: false, editable: false },
    [WorkflowUIField.INVOICE_DATE]: { visible: false, editable: false },
    [WorkflowUIField.DUE_DATE]: { visible: false, editable: false },
    [WorkflowUIField.TOTAL_AMOUNT]: { visible: false, editable: false },
    [WorkflowUIField.SUPPLIER_ABN]: { visible: false, editable: false },
    [WorkflowUIField.STORE_ID]: { visible: false, editable: false },
    [WorkflowUIField.PO]: { visible: false, editable: false },
    [WorkflowUIField.SO]: { visible: false, editable: false },
    [WorkflowUIField.IR]: { visible: false, editable: false },
    [WorkflowUIField.INV]: { visible: false, editable: false },
    [WorkflowUIField.CLAIM]: { visible: false, editable: false },
    [WorkflowUIField.CN]: { visible: false, editable: false },
    [WorkflowUIField.CREDIT_AMOUNT]: { visible: false, editable: false },
    [WorkflowUIField.NOTES]: { visible: false, editable: false }
  };

  return {
    [UserRole.ADMINISTRATOR]: { ...defaultPermissions, ...adminPermissions },
    [UserRole.FINANCE]: { ...defaultPermissions, ...financePermissions },
    [UserRole.STOCK]: { ...defaultPermissions, ...stockPermissions }
  };
};

// Complete workflow field permissions configuration
export const WORKFLOW_FIELD_PERMISSIONS: WorkflowConfiguration = {
  // Status 1: UNPROCESSED - Initial invoice entry
  [InvoiceStatus.UNPROCESSED]: createFieldPermissions(
    {
      [WorkflowUIField.CLASSIFICATION]: { visible: true, editable: true },
      [WorkflowUIField.NEW_STOCK]: { visible: false, editable: false },
      [WorkflowUIField.PRODUCT_ADDED]: { visible: false, editable: false },
      [WorkflowUIField.SUPPLIER]: { visible: true, editable: true },
      [WorkflowUIField.INVOICE_NUMBER]: { visible: true, editable: true },
      [WorkflowUIField.INVOICE_DATE]: { visible: true, editable: true },
      [WorkflowUIField.DUE_DATE]: { visible: true, editable: true },
      [WorkflowUIField.TOTAL_AMOUNT]: { visible: true, editable: true },
      [WorkflowUIField.SUPPLIER_ABN]: { visible: true, editable: true },
      [WorkflowUIField.STORE_ID]: { visible: false, editable: false },
      [WorkflowUIField.PO]: { visible: false, editable: false },
      [WorkflowUIField.SO]: { visible: false, editable: false },
      [WorkflowUIField.IR]: { visible: false, editable: false },
      [WorkflowUIField.INV]: { visible: false, editable: false },
      [WorkflowUIField.CLAIM]: { visible: false, editable: false },
      [WorkflowUIField.CN]: { visible: false, editable: false },
      [WorkflowUIField.CREDIT_AMOUNT]: { visible: false, editable: false },
      [WorkflowUIField.NOTES]: { visible: true, editable: true }
    },
    {
      [WorkflowUIField.CLASSIFICATION]: { visible: true, editable: true },
      [WorkflowUIField.NEW_STOCK]: { visible: false, editable: false },
      [WorkflowUIField.PRODUCT_ADDED]: { visible: false, editable: false },
      [WorkflowUIField.SUPPLIER]: { visible: true, editable: true },
      [WorkflowUIField.INVOICE_NUMBER]: { visible: true, editable: true },
      [WorkflowUIField.INVOICE_DATE]: { visible: true, editable: true },
      [WorkflowUIField.DUE_DATE]: { visible: true, editable: true },
      [WorkflowUIField.TOTAL_AMOUNT]: { visible: true, editable: true },
      [WorkflowUIField.SUPPLIER_ABN]: { visible: true, editable: true },
      [WorkflowUIField.STORE_ID]: { visible: false, editable: false },
      [WorkflowUIField.PO]: { visible: false, editable: false },
      [WorkflowUIField.SO]: { visible: false, editable: false },
      [WorkflowUIField.IR]: { visible: false, editable: false },
      [WorkflowUIField.INV]: { visible: false, editable: false },
      [WorkflowUIField.CLAIM]: { visible: false, editable: false },
      [WorkflowUIField.CN]: { visible: false, editable: false },
      [WorkflowUIField.CREDIT_AMOUNT]: { visible: false, editable: false },
      [WorkflowUIField.NOTES]: { visible: true, editable: true }
    },
    {
      [WorkflowUIField.CLASSIFICATION]: { visible: false, editable: false },
      [WorkflowUIField.NEW_STOCK]: { visible: false, editable: false },
      [WorkflowUIField.PRODUCT_ADDED]: { visible: false, editable: false },
      [WorkflowUIField.SUPPLIER]: { visible: false, editable: false },
      [WorkflowUIField.INVOICE_NUMBER]: { visible: false, editable: false },
      [WorkflowUIField.INVOICE_DATE]: { visible: false, editable: false },
      [WorkflowUIField.DUE_DATE]: { visible: false, editable: false },
      [WorkflowUIField.TOTAL_AMOUNT]: { visible: false, editable: false },
      [WorkflowUIField.SUPPLIER_ABN]: { visible: false, editable: false },
      [WorkflowUIField.STORE_ID]: { visible: false, editable: false },
      [WorkflowUIField.PO]: { visible: false, editable: false },
      [WorkflowUIField.SO]: { visible: false, editable: false },
      [WorkflowUIField.IR]: { visible: false, editable: false },
      [WorkflowUIField.INV]: { visible: false, editable: false },
      [WorkflowUIField.CLAIM]: { visible: false, editable: false },
      [WorkflowUIField.CN]: { visible: false, editable: false },
      [WorkflowUIField.CREDIT_AMOUNT]: { visible: false, editable: false },
      [WorkflowUIField.NOTES]: { visible: false, editable: false }
    }
  ),

  // Status 2: ADDING - Adding new stock
  [InvoiceStatus.ADDING]: createFieldPermissions(
    {
      [WorkflowUIField.CLASSIFICATION]: { visible: true, editable: false },
      [WorkflowUIField.SUPPLIER]: { visible: true, editable: true },
      [WorkflowUIField.INVOICE_NUMBER]: { visible: true, editable: true },
      [WorkflowUIField.INVOICE_DATE]: { visible: true, editable: true },
      [WorkflowUIField.DUE_DATE]: { visible: true, editable: true },
      [WorkflowUIField.TOTAL_AMOUNT]: { visible: true, editable: true },
      [WorkflowUIField.SUPPLIER_ABN]: { visible: true, editable: true },
      [WorkflowUIField.STORE_ID]: { visible: true, editable: true },
      [WorkflowUIField.PO]: { visible: true, editable: true },
      [WorkflowUIField.SO]: { visible: true, editable: true },
      [WorkflowUIField.NEW_STOCK]: { visible: true, editable: true },
      [WorkflowUIField.NOTES]: { visible: true, editable: true }
    },
    {
      [WorkflowUIField.CLASSIFICATION]: { visible: true, editable: false },
      [WorkflowUIField.SUPPLIER]: { visible: true, editable: true },
      [WorkflowUIField.INVOICE_NUMBER]: { visible: true, editable: true },
      [WorkflowUIField.INVOICE_DATE]: { visible: true, editable: true },
      [WorkflowUIField.DUE_DATE]: { visible: true, editable: true },
      [WorkflowUIField.TOTAL_AMOUNT]: { visible: true, editable: true },
      [WorkflowUIField.SUPPLIER_ABN]: { visible: true, editable: true },
      [WorkflowUIField.STORE_ID]: { visible: true, editable: true },
      [WorkflowUIField.PO]: { visible: true, editable: true },
      [WorkflowUIField.SO]: { visible: true, editable: true },
      [WorkflowUIField.NEW_STOCK]: { visible: true, editable: true },
      [WorkflowUIField.NOTES]: { visible: true, editable: true }
    },
    {
      [WorkflowUIField.CLASSIFICATION]: { visible: true, editable: false },
      [WorkflowUIField.SUPPLIER]: { visible: true, editable: false },
      [WorkflowUIField.INVOICE_NUMBER]: { visible: true, editable: false },
      [WorkflowUIField.INVOICE_DATE]: { visible: true, editable: false },
      [WorkflowUIField.DUE_DATE]: { visible: true, editable: false },
      [WorkflowUIField.TOTAL_AMOUNT]: { visible: true, editable: false },
      [WorkflowUIField.SUPPLIER_ABN]: { visible: true, editable: false },
      [WorkflowUIField.STORE_ID]: { visible: true, editable: true },
      [WorkflowUIField.PO]: { visible: true, editable: true },
      [WorkflowUIField.SO]: { visible: true, editable: true },
      [WorkflowUIField.NEW_STOCK]: { visible: true, editable: true },
      [WorkflowUIField.NOTES]: { visible: true, editable: true }
    }
  ),

  // Status 3: NEW_PRODUCTS - New products added
  [InvoiceStatus.NEW_PRODUCTS]: createFieldPermissions(
    {
      [WorkflowUIField.CLASSIFICATION]: { visible: true, editable: false },
      [WorkflowUIField.SUPPLIER]: { visible: true, editable: true },
      [WorkflowUIField.INVOICE_NUMBER]: { visible: true, editable: true },
      [WorkflowUIField.INVOICE_DATE]: { visible: true, editable: true },
      [WorkflowUIField.DUE_DATE]: { visible: true, editable: true },
      [WorkflowUIField.TOTAL_AMOUNT]: { visible: true, editable: true },
      [WorkflowUIField.SUPPLIER_ABN]: { visible: true, editable: true },
      [WorkflowUIField.STORE_ID]: { visible: true, editable: true },
      [WorkflowUIField.PO]: { visible: true, editable: true },
      [WorkflowUIField.SO]: { visible: true, editable: true },
      [WorkflowUIField.NEW_STOCK]: { visible: true, editable: true },
      [WorkflowUIField.PRODUCT_ADDED]: { visible: true, editable: true },
      [WorkflowUIField.NOTES]: { visible: true, editable: true }
    },
    {
      [WorkflowUIField.CLASSIFICATION]: { visible: true, editable: false },
      [WorkflowUIField.SUPPLIER]: { visible: true, editable: false },
      [WorkflowUIField.INVOICE_NUMBER]: { visible: true, editable: false },
      [WorkflowUIField.INVOICE_DATE]: { visible: true, editable: false },
      [WorkflowUIField.DUE_DATE]: { visible: true, editable: false },
      [WorkflowUIField.TOTAL_AMOUNT]: { visible: true, editable: false },
      [WorkflowUIField.SUPPLIER_ABN]: { visible: true, editable: false },
      [WorkflowUIField.STORE_ID]: { visible: true, editable: false },
      [WorkflowUIField.PO]: { visible: true, editable: false },
      [WorkflowUIField.SO]: { visible: true, editable: false },
      [WorkflowUIField.NEW_STOCK]: { visible: true, editable: false },
      [WorkflowUIField.PRODUCT_ADDED]: { visible: true, editable: true },
      [WorkflowUIField.NOTES]: { visible: true, editable: true }
    },
    {
      [WorkflowUIField.CLASSIFICATION]: { visible: true, editable: false },
      [WorkflowUIField.SUPPLIER]: { visible: true, editable: false },
      [WorkflowUIField.INVOICE_NUMBER]: { visible: true, editable: false },
      [WorkflowUIField.INVOICE_DATE]: { visible: true, editable: false },
      [WorkflowUIField.DUE_DATE]: { visible: true, editable: false },
      [WorkflowUIField.TOTAL_AMOUNT]: { visible: true, editable: false },
      [WorkflowUIField.SUPPLIER_ABN]: { visible: true, editable: false },
      [WorkflowUIField.STORE_ID]: { visible: true, editable: false },
      [WorkflowUIField.PO]: { visible: true, editable: false },
      [WorkflowUIField.SO]: { visible: true, editable: false },
      [WorkflowUIField.NEW_STOCK]: { visible: true, editable: false },
      [WorkflowUIField.PRODUCT_ADDED]: { visible: true, editable: true },
      [WorkflowUIField.NOTES]: { visible: true, editable: true }
    }
  ),

  // Status 4: IR - Invoice Review
  [InvoiceStatus.IR]: createFieldPermissions(
    {
      [WorkflowUIField.CLASSIFICATION]: { visible: true, editable: false },
      [WorkflowUIField.SUPPLIER]: { visible: true, editable: true },
      [WorkflowUIField.INVOICE_NUMBER]: { visible: true, editable: true },
      [WorkflowUIField.INVOICE_DATE]: { visible: true, editable: true },
      [WorkflowUIField.DUE_DATE]: { visible: true, editable: true },
      [WorkflowUIField.TOTAL_AMOUNT]: { visible: true, editable: true },
      [WorkflowUIField.SUPPLIER_ABN]: { visible: true, editable: true },
      [WorkflowUIField.STORE_ID]: { visible: true, editable: true },
      [WorkflowUIField.PO]: { visible: true, editable: true },
      [WorkflowUIField.SO]: { visible: true, editable: true },
      [WorkflowUIField.IR]: { visible: true, editable: true },
      [WorkflowUIField.CLAIM]: { visible: false, editable: false },
      [WorkflowUIField.CN]: { visible: false, editable: false },
      [WorkflowUIField.CREDIT_AMOUNT]: { visible: false, editable: false },
      [WorkflowUIField.NOTES]: { visible: true, editable: true }
    },
    {
      [WorkflowUIField.CLASSIFICATION]: { visible: true, editable: false },
      [WorkflowUIField.SUPPLIER]: { visible: true, editable: true },
      [WorkflowUIField.INVOICE_NUMBER]: { visible: true, editable: true },
      [WorkflowUIField.INVOICE_DATE]: { visible: true, editable: true },
      [WorkflowUIField.DUE_DATE]: { visible: true, editable: true },
      [WorkflowUIField.TOTAL_AMOUNT]: { visible: true, editable: true },
      [WorkflowUIField.SUPPLIER_ABN]: { visible: true, editable: true },
      [WorkflowUIField.STORE_ID]: { visible: true, editable: true },
      [WorkflowUIField.PO]: { visible: true, editable: true },
      [WorkflowUIField.SO]: { visible: true, editable: true },
      [WorkflowUIField.IR]: { visible: true, editable: true },
      [WorkflowUIField.CLAIM]: { visible: false, editable: false },
      [WorkflowUIField.CN]: { visible: false, editable: false },
      [WorkflowUIField.CREDIT_AMOUNT]: { visible: false, editable: false },
      [WorkflowUIField.NOTES]: { visible: true, editable: true }
    },
    {
      [WorkflowUIField.CLASSIFICATION]: { visible: true, editable: false },
      [WorkflowUIField.SUPPLIER]: { visible: true, editable: false },
      [WorkflowUIField.INVOICE_NUMBER]: { visible: true, editable: false },
      [WorkflowUIField.INVOICE_DATE]: { visible: true, editable: false },
      [WorkflowUIField.DUE_DATE]: { visible: true, editable: false },
      [WorkflowUIField.TOTAL_AMOUNT]: { visible: true, editable: false },
      [WorkflowUIField.SUPPLIER_ABN]: { visible: true, editable: false },
      [WorkflowUIField.STORE_ID]: { visible: true, editable: false },
      [WorkflowUIField.PO]: { visible: true, editable: false },
      [WorkflowUIField.SO]: { visible: true, editable: false },
      [WorkflowUIField.IR]: { visible: true, editable: true },
      [WorkflowUIField.CLAIM]: { visible: false, editable: false },
      [WorkflowUIField.CN]: { visible: false, editable: false },
      [WorkflowUIField.CREDIT_AMOUNT]: { visible: false, editable: false },
      [WorkflowUIField.NOTES]: { visible: true, editable: true }
    }
  ),

  // Status 5: INVOICING - Invoice processing
  [InvoiceStatus.INVOICING]: createFieldPermissions(
    {
      [WorkflowUIField.CLASSIFICATION]: { visible: true, editable: false },
      [WorkflowUIField.SUPPLIER]: { visible: true, editable: true },
      [WorkflowUIField.INVOICE_NUMBER]: { visible: true, editable: true },
      [WorkflowUIField.INVOICE_DATE]: { visible: true, editable: true },
      [WorkflowUIField.DUE_DATE]: { visible: true, editable: true },
      [WorkflowUIField.TOTAL_AMOUNT]: { visible: true, editable: true },
      [WorkflowUIField.SUPPLIER_ABN]: { visible: true, editable: true },
      [WorkflowUIField.STORE_ID]: { visible: true, editable: true },
      [WorkflowUIField.PO]: { visible: true, editable: true },
      [WorkflowUIField.SO]: { visible: true, editable: true },
      [WorkflowUIField.IR]: { visible: true, editable: true },
      [WorkflowUIField.INV]: { visible: true, editable: true },
      [WorkflowUIField.CLAIM]: { visible: false, editable: false },
      [WorkflowUIField.CN]: { visible: false, editable: false },
      [WorkflowUIField.CREDIT_AMOUNT]: { visible: false, editable: false },
      [WorkflowUIField.NOTES]: { visible: true, editable: true }
    },
    {
      [WorkflowUIField.CLASSIFICATION]: { visible: true, editable: false },
      [WorkflowUIField.SUPPLIER]: { visible: true, editable: true },
      [WorkflowUIField.INVOICE_NUMBER]: { visible: true, editable: true },
      [WorkflowUIField.INVOICE_DATE]: { visible: true, editable: true },
      [WorkflowUIField.DUE_DATE]: { visible: true, editable: true },
      [WorkflowUIField.TOTAL_AMOUNT]: { visible: true, editable: true },
      [WorkflowUIField.SUPPLIER_ABN]: { visible: true, editable: true },
      [WorkflowUIField.STORE_ID]: { visible: true, editable: true },
      [WorkflowUIField.PO]: { visible: true, editable: true },
      [WorkflowUIField.SO]: { visible: true, editable: true },
      [WorkflowUIField.IR]: { visible: true, editable: true },
      [WorkflowUIField.INV]: { visible: true, editable: true },
      [WorkflowUIField.CLAIM]: { visible: false, editable: false },
      [WorkflowUIField.CN]: { visible: false, editable: false },
      [WorkflowUIField.CREDIT_AMOUNT]: { visible: false, editable: false },
      [WorkflowUIField.NOTES]: { visible: true, editable: true }
    },
    {
      [WorkflowUIField.CLASSIFICATION]: { visible: true, editable: false },
      [WorkflowUIField.SUPPLIER]: { visible: true, editable: false },
      [WorkflowUIField.INVOICE_NUMBER]: { visible: true, editable: false },
      [WorkflowUIField.INVOICE_DATE]: { visible: true, editable: false },
      [WorkflowUIField.DUE_DATE]: { visible: true, editable: false },
      [WorkflowUIField.TOTAL_AMOUNT]: { visible: true, editable: false },
      [WorkflowUIField.SUPPLIER_ABN]: { visible: true, editable: false },
      [WorkflowUIField.STORE_ID]: { visible: true, editable: false },
      [WorkflowUIField.PO]: { visible: true, editable: false },
      [WorkflowUIField.SO]: { visible: true, editable: false },
      [WorkflowUIField.IR]: { visible: true, editable: false },
      [WorkflowUIField.INV]: { visible: true, editable: true },
      [WorkflowUIField.CLAIM]: { visible: false, editable: false },
      [WorkflowUIField.CN]: { visible: false, editable: false },
      [WorkflowUIField.CREDIT_AMOUNT]: { visible: false, editable: false },
      [WorkflowUIField.NOTES]: { visible: true, editable: true }
    }
  ),

  // Status 6: HEAD_OFFICE_STOCK - Head office stock processing
  [InvoiceStatus.HEAD_OFFICE_STOCK]: createFieldPermissions(
    {
      [WorkflowUIField.CLASSIFICATION]: { visible: true, editable: false },
      [WorkflowUIField.SUPPLIER]: { visible: true, editable: true },
      [WorkflowUIField.INVOICE_NUMBER]: { visible: true, editable: true },
      [WorkflowUIField.INVOICE_DATE]: { visible: true, editable: true },
      [WorkflowUIField.DUE_DATE]: { visible: true, editable: true },
      [WorkflowUIField.TOTAL_AMOUNT]: { visible: true, editable: true },
      [WorkflowUIField.SUPPLIER_ABN]: { visible: true, editable: true },
      [WorkflowUIField.IR]: { visible: true, editable: true },
      [WorkflowUIField.NOTES]: { visible: true, editable: true }
    },
    {
      [WorkflowUIField.CLASSIFICATION]: { visible: true, editable: false },
      [WorkflowUIField.SUPPLIER]: { visible: true, editable: true },
      [WorkflowUIField.INVOICE_NUMBER]: { visible: true, editable: true },
      [WorkflowUIField.INVOICE_DATE]: { visible: true, editable: true },
      [WorkflowUIField.DUE_DATE]: { visible: true, editable: true },
      [WorkflowUIField.TOTAL_AMOUNT]: { visible: true, editable: true },
      [WorkflowUIField.SUPPLIER_ABN]: { visible: true, editable: true },
      [WorkflowUIField.IR]: { visible: true, editable: true },
      [WorkflowUIField.NOTES]: { visible: true, editable: true }
    },
    {
      [WorkflowUIField.CLASSIFICATION]: { visible: true, editable: false },
      [WorkflowUIField.SUPPLIER]: { visible: true, editable: false },
      [WorkflowUIField.INVOICE_NUMBER]: { visible: true, editable: false },
      [WorkflowUIField.INVOICE_DATE]: { visible: true, editable: false },
      [WorkflowUIField.DUE_DATE]: { visible: true, editable: false },
      [WorkflowUIField.TOTAL_AMOUNT]: { visible: true, editable: false },
      [WorkflowUIField.SUPPLIER_ABN]: { visible: true, editable: false },
      [WorkflowUIField.IR]: { visible: true, editable: false },
      [WorkflowUIField.NOTES]: { visible: true, editable: true }
    }
  ),

  // Status 7: EXPENSE_INVOICES - Expense invoice processing
  [InvoiceStatus.EXPENSE_INVOICES]: createFieldPermissions(
    {
      [WorkflowUIField.CLASSIFICATION]: { visible: true, editable: false },
      [WorkflowUIField.SUPPLIER]: { visible: true, editable: true },
      [WorkflowUIField.INVOICE_NUMBER]: { visible: true, editable: true },
      [WorkflowUIField.INVOICE_DATE]: { visible: true, editable: true },
      [WorkflowUIField.DUE_DATE]: { visible: true, editable: true },
      [WorkflowUIField.TOTAL_AMOUNT]: { visible: true, editable: true },
      [WorkflowUIField.SUPPLIER_ABN]: { visible: true, editable: true },
      [WorkflowUIField.NOTES]: { visible: true, editable: true }
    },
    {
      [WorkflowUIField.CLASSIFICATION]: { visible: true, editable: false },
      [WorkflowUIField.SUPPLIER]: { visible: true, editable: true },
      [WorkflowUIField.INVOICE_NUMBER]: { visible: true, editable: true },
      [WorkflowUIField.INVOICE_DATE]: { visible: true, editable: true },
      [WorkflowUIField.DUE_DATE]: { visible: true, editable: true },
      [WorkflowUIField.TOTAL_AMOUNT]: { visible: true, editable: true },
      [WorkflowUIField.SUPPLIER_ABN]: { visible: true, editable: true },
      [WorkflowUIField.NOTES]: { visible: true, editable: true }
    },
    {
      [WorkflowUIField.CLASSIFICATION]: { visible: true, editable: false },
      [WorkflowUIField.SUPPLIER]: { visible: true, editable: false },
      [WorkflowUIField.INVOICE_NUMBER]: { visible: true, editable: false },
      [WorkflowUIField.INVOICE_DATE]: { visible: true, editable: false },
      [WorkflowUIField.DUE_DATE]: { visible: true, editable: false },
      [WorkflowUIField.TOTAL_AMOUNT]: { visible: true, editable: false },
      [WorkflowUIField.SUPPLIER_ABN]: { visible: true, editable: false },
      [WorkflowUIField.NOTES]: { visible: true, editable: true }
    }
  ),

  // Status 8: ENTERING_STOCK - Stock data entry
  [InvoiceStatus.ENTERING_STOCK]: createFieldPermissions(
    {
      [WorkflowUIField.CLASSIFICATION]: { visible: true, editable: false },
      [WorkflowUIField.SUPPLIER]: { visible: true, editable: true },
      [WorkflowUIField.INVOICE_NUMBER]: { visible: true, editable: true },
      [WorkflowUIField.INVOICE_DATE]: { visible: true, editable: true },
      [WorkflowUIField.DUE_DATE]: { visible: true, editable: true },
      [WorkflowUIField.TOTAL_AMOUNT]: { visible: true, editable: true },
      [WorkflowUIField.SUPPLIER_ABN]: { visible: true, editable: true },
      [WorkflowUIField.STORE_ID]: { visible: true, editable: true },
      [WorkflowUIField.PO]: { visible: true, editable: true },
      [WorkflowUIField.SO]: { visible: true, editable: true },
      [WorkflowUIField.IR]: { visible: true, editable: true },
      [WorkflowUIField.INV]: { visible: true, editable: true },
      [WorkflowUIField.CLAIM]: { visible: false, editable: false },
      [WorkflowUIField.CN]: { visible: false, editable: false },
      [WorkflowUIField.CREDIT_AMOUNT]: { visible: false, editable: false },
      [WorkflowUIField.NOTES]: { visible: true, editable: true }
    },
    {
      [WorkflowUIField.CLASSIFICATION]: { visible: true, editable: false },
      [WorkflowUIField.SUPPLIER]: { visible: true, editable: true },
      [WorkflowUIField.INVOICE_NUMBER]: { visible: true, editable: true },
      [WorkflowUIField.INVOICE_DATE]: { visible: true, editable: true },
      [WorkflowUIField.DUE_DATE]: { visible: true, editable: true },
      [WorkflowUIField.TOTAL_AMOUNT]: { visible: true, editable: true },
      [WorkflowUIField.SUPPLIER_ABN]: { visible: true, editable: true },
      [WorkflowUIField.STORE_ID]: { visible: true, editable: true },
      [WorkflowUIField.PO]: { visible: true, editable: true },
      [WorkflowUIField.SO]: { visible: true, editable: true },
      [WorkflowUIField.IR]: { visible: true, editable: true },
      [WorkflowUIField.INV]: { visible: true, editable: true },
      [WorkflowUIField.CLAIM]: { visible: false, editable: false },
      [WorkflowUIField.CN]: { visible: false, editable: false },
      [WorkflowUIField.CREDIT_AMOUNT]: { visible: false, editable: false },
      [WorkflowUIField.NOTES]: { visible: true, editable: true }
    },
    {
      [WorkflowUIField.CLASSIFICATION]: { visible: true, editable: false },
      [WorkflowUIField.SUPPLIER]: { visible: true, editable: false },
      [WorkflowUIField.INVOICE_NUMBER]: { visible: true, editable: false },
      [WorkflowUIField.INVOICE_DATE]: { visible: true, editable: false },
      [WorkflowUIField.DUE_DATE]: { visible: true, editable: false },
      [WorkflowUIField.TOTAL_AMOUNT]: { visible: true, editable: false },
      [WorkflowUIField.SUPPLIER_ABN]: { visible: true, editable: false },
      [WorkflowUIField.STORE_ID]: { visible: true, editable: false },
      [WorkflowUIField.PO]: { visible: true, editable: false },
      [WorkflowUIField.SO]: { visible: true, editable: false },
      [WorkflowUIField.IR]: { visible: true, editable: false },
      [WorkflowUIField.INV]: { visible: true, editable: false },
      [WorkflowUIField.CLAIM]: { visible: false, editable: false },
      [WorkflowUIField.CN]: { visible: false, editable: false },
      [WorkflowUIField.CREDIT_AMOUNT]: { visible: false, editable: false },
      [WorkflowUIField.NOTES]: { visible: true, editable: true }
    }
  ),

  // Status 9: ENTERING_EXPENSE - Expense data entry
  [InvoiceStatus.ENTERING_EXPENSE]: createFieldPermissions(
    {
      [WorkflowUIField.CLASSIFICATION]: { visible: true, editable: false },
      [WorkflowUIField.SUPPLIER]: { visible: true, editable: true },
      [WorkflowUIField.INVOICE_NUMBER]: { visible: true, editable: true },
      [WorkflowUIField.INVOICE_DATE]: { visible: true, editable: true },
      [WorkflowUIField.DUE_DATE]: { visible: true, editable: true },
      [WorkflowUIField.TOTAL_AMOUNT]: { visible: true, editable: true },
      [WorkflowUIField.SUPPLIER_ABN]: { visible: true, editable: true },
      [WorkflowUIField.NOTES]: { visible: true, editable: true }
    },
    {
      [WorkflowUIField.CLASSIFICATION]: { visible: true, editable: false },
      [WorkflowUIField.SUPPLIER]: { visible: true, editable: true },
      [WorkflowUIField.INVOICE_NUMBER]: { visible: true, editable: true },
      [WorkflowUIField.INVOICE_DATE]: { visible: true, editable: true },
      [WorkflowUIField.DUE_DATE]: { visible: true, editable: true },
      [WorkflowUIField.TOTAL_AMOUNT]: { visible: true, editable: true },
      [WorkflowUIField.SUPPLIER_ABN]: { visible: true, editable: true },
      [WorkflowUIField.NOTES]: { visible: true, editable: true }
    },
    {
      [WorkflowUIField.CLASSIFICATION]: { visible: true, editable: false },
      [WorkflowUIField.SUPPLIER]: { visible: true, editable: false },
      [WorkflowUIField.INVOICE_NUMBER]: { visible: true, editable: false },
      [WorkflowUIField.INVOICE_DATE]: { visible: true, editable: false },
      [WorkflowUIField.DUE_DATE]: { visible: true, editable: false },
      [WorkflowUIField.TOTAL_AMOUNT]: { visible: true, editable: false },
      [WorkflowUIField.SUPPLIER_ABN]: { visible: true, editable: false },
      [WorkflowUIField.NOTES]: { visible: true, editable: true }
    }
  ),

  // Status 10: CREDIT_NOTE - Credit note processing
  [InvoiceStatus.CREDIT_NOTE]: createFieldPermissions(
    {
      [WorkflowUIField.CLASSIFICATION]: { visible: true, editable: false },
      [WorkflowUIField.SUPPLIER]: { visible: true, editable: true },
      [WorkflowUIField.INVOICE_NUMBER]: { visible: true, editable: true },
      [WorkflowUIField.INVOICE_DATE]: { visible: true, editable: true },
      [WorkflowUIField.DUE_DATE]: { visible: true, editable: true },
      [WorkflowUIField.TOTAL_AMOUNT]: { visible: true, editable: true },
      [WorkflowUIField.SUPPLIER_ABN]: { visible: true, editable: true },
      [WorkflowUIField.STORE_ID]: { visible: true, editable: true },
      [WorkflowUIField.CLAIM]: { visible: true, editable: true },
      [WorkflowUIField.CN]: { visible: true, editable: true },
      [WorkflowUIField.CREDIT_AMOUNT]: { visible: true, editable: true },
      [WorkflowUIField.NOTES]: { visible: true, editable: true }
    },
    {
      [WorkflowUIField.CLASSIFICATION]: { visible: true, editable: false },
      [WorkflowUIField.SUPPLIER]: { visible: true, editable: true },
      [WorkflowUIField.INVOICE_NUMBER]: { visible: true, editable: true },
      [WorkflowUIField.INVOICE_DATE]: { visible: true, editable: true },
      [WorkflowUIField.DUE_DATE]: { visible: true, editable: true },
      [WorkflowUIField.TOTAL_AMOUNT]: { visible: true, editable: true },
      [WorkflowUIField.SUPPLIER_ABN]: { visible: true, editable: true },
      [WorkflowUIField.STORE_ID]: { visible: true, editable: true },
      [WorkflowUIField.CLAIM]: { visible: true, editable: true },
      [WorkflowUIField.CN]: { visible: true, editable: true },
      [WorkflowUIField.CREDIT_AMOUNT]: { visible: true, editable: true },
      [WorkflowUIField.NOTES]: { visible: true, editable: true }
    },
    {
      [WorkflowUIField.CLASSIFICATION]: { visible: true, editable: false },
      [WorkflowUIField.SUPPLIER]: { visible: true, editable: false },
      [WorkflowUIField.INVOICE_NUMBER]: { visible: true, editable: false },
      [WorkflowUIField.INVOICE_DATE]: { visible: true, editable: false },
      [WorkflowUIField.DUE_DATE]: { visible: true, editable: false },
      [WorkflowUIField.TOTAL_AMOUNT]: { visible: true, editable: false },
      [WorkflowUIField.SUPPLIER_ABN]: { visible: true, editable: false },
      [WorkflowUIField.STORE_ID]: { visible: true, editable: false },
      [WorkflowUIField.CLAIM]: { visible: true, editable: false },
      [WorkflowUIField.CN]: { visible: true, editable: false },
      [WorkflowUIField.CREDIT_AMOUNT]: { visible: true, editable: false },
      [WorkflowUIField.NOTES]: { visible: true, editable: true }
    }
  ),

  // Status 11: CREDIT_ENTERING - Credit note data entry
  [InvoiceStatus.CREDIT_ENTERING]: createFieldPermissions(
    {
      [WorkflowUIField.CLASSIFICATION]: { visible: true, editable: false },
      [WorkflowUIField.SUPPLIER]: { visible: true, editable: true },
      [WorkflowUIField.INVOICE_NUMBER]: { visible: true, editable: true },
      [WorkflowUIField.INVOICE_DATE]: { visible: true, editable: true },
      [WorkflowUIField.DUE_DATE]: { visible: true, editable: true },
      [WorkflowUIField.TOTAL_AMOUNT]: { visible: true, editable: true },
      [WorkflowUIField.SUPPLIER_ABN]: { visible: true, editable: true },
      [WorkflowUIField.STORE_ID]: { visible: true, editable: true },
      [WorkflowUIField.CLAIM]: { visible: true, editable: true },
      [WorkflowUIField.CN]: { visible: true, editable: true },
      [WorkflowUIField.CREDIT_AMOUNT]: { visible: true, editable: true },
      [WorkflowUIField.NOTES]: { visible: true, editable: true }
    },
    {
      [WorkflowUIField.CLASSIFICATION]: { visible: true, editable: false },
      [WorkflowUIField.SUPPLIER]: { visible: true, editable: true },
      [WorkflowUIField.INVOICE_NUMBER]: { visible: true, editable: true },
      [WorkflowUIField.INVOICE_DATE]: { visible: true, editable: true },
      [WorkflowUIField.DUE_DATE]: { visible: true, editable: true },
      [WorkflowUIField.TOTAL_AMOUNT]: { visible: true, editable: true },
      [WorkflowUIField.SUPPLIER_ABN]: { visible: true, editable: true },
      [WorkflowUIField.STORE_ID]: { visible: true, editable: true },
      [WorkflowUIField.CLAIM]: { visible: true, editable: true },
      [WorkflowUIField.CN]: { visible: true, editable: true },
      [WorkflowUIField.CREDIT_AMOUNT]: { visible: true, editable: true },
      [WorkflowUIField.NOTES]: { visible: true, editable: true }
    },
    {
      [WorkflowUIField.CLASSIFICATION]: { visible: true, editable: false },
      [WorkflowUIField.SUPPLIER]: { visible: true, editable: false },
      [WorkflowUIField.INVOICE_NUMBER]: { visible: true, editable: false },
      [WorkflowUIField.INVOICE_DATE]: { visible: true, editable: false },
      [WorkflowUIField.DUE_DATE]: { visible: true, editable: false },
      [WorkflowUIField.TOTAL_AMOUNT]: { visible: true, editable: false },
      [WorkflowUIField.SUPPLIER_ABN]: { visible: true, editable: false },
      [WorkflowUIField.STORE_ID]: { visible: true, editable: false },
      [WorkflowUIField.CLAIM]: { visible: true, editable: false },
      [WorkflowUIField.CN]: { visible: true, editable: false },
      [WorkflowUIField.CREDIT_AMOUNT]: { visible: true, editable: false },
      [WorkflowUIField.NOTES]: { visible: true, editable: true }
    }
  ),

  // Status 12: COMPLETED - Final completed state
  [InvoiceStatus.COMPLETED]: createFieldPermissions(
    {
      [WorkflowUIField.CLASSIFICATION]: { visible: true, editable: false },
      [WorkflowUIField.SUPPLIER]: { visible: true, editable: true },
      [WorkflowUIField.INVOICE_NUMBER]: { visible: true, editable: true },
      [WorkflowUIField.INVOICE_DATE]: { visible: true, editable: true },
      [WorkflowUIField.DUE_DATE]: { visible: true, editable: true },
      [WorkflowUIField.TOTAL_AMOUNT]: { visible: true, editable: true },
      [WorkflowUIField.SUPPLIER_ABN]: { visible: true, editable: true },
      [WorkflowUIField.STORE_ID]: { visible: true, editable: true },
      [WorkflowUIField.PO]: { visible: true, editable: true },
      [WorkflowUIField.SO]: { visible: true, editable: true },
      [WorkflowUIField.IR]: { visible: true, editable: true },
      [WorkflowUIField.INV]: { visible: true, editable: true },
      [WorkflowUIField.CLAIM]: { visible: true, editable: true },
      [WorkflowUIField.CN]: { visible: true, editable: true },
      [WorkflowUIField.CREDIT_AMOUNT]: { visible: true, editable: true },
      [WorkflowUIField.NOTES]: { visible: true, editable: true }
    },
    {
      [WorkflowUIField.CLASSIFICATION]: { visible: true, editable: false },
      [WorkflowUIField.SUPPLIER]: { visible: true, editable: true },
      [WorkflowUIField.INVOICE_NUMBER]: { visible: true, editable: true },
      [WorkflowUIField.INVOICE_DATE]: { visible: true, editable: true },
      [WorkflowUIField.DUE_DATE]: { visible: true, editable: true },
      [WorkflowUIField.TOTAL_AMOUNT]: { visible: true, editable: true },
      [WorkflowUIField.SUPPLIER_ABN]: { visible: true, editable: true },
      [WorkflowUIField.STORE_ID]: { visible: true, editable: true },
      [WorkflowUIField.PO]: { visible: true, editable: true },
      [WorkflowUIField.SO]: { visible: true, editable: true },
      [WorkflowUIField.IR]: { visible: true, editable: true },
      [WorkflowUIField.INV]: { visible: true, editable: true },
      [WorkflowUIField.CLAIM]: { visible: true, editable: true },
      [WorkflowUIField.CN]: { visible: true, editable: true },
      [WorkflowUIField.CREDIT_AMOUNT]: { visible: true, editable: true },
      [WorkflowUIField.NOTES]: { visible: true, editable: true }
    },
    {
      [WorkflowUIField.CLASSIFICATION]: { visible: true, editable: false },
      [WorkflowUIField.SUPPLIER]: { visible: true, editable: false },
      [WorkflowUIField.INVOICE_NUMBER]: { visible: true, editable: false },
      [WorkflowUIField.INVOICE_DATE]: { visible: true, editable: false },
      [WorkflowUIField.DUE_DATE]: { visible: true, editable: false },
      [WorkflowUIField.TOTAL_AMOUNT]: { visible: true, editable: false },
      [WorkflowUIField.SUPPLIER_ABN]: { visible: true, editable: false },
      [WorkflowUIField.STORE_ID]: { visible: true, editable: false },
      [WorkflowUIField.PO]: { visible: true, editable: false },
      [WorkflowUIField.SO]: { visible: true, editable: false },
      [WorkflowUIField.IR]: { visible: true, editable: false },
      [WorkflowUIField.INV]: { visible: true, editable: false },
      [WorkflowUIField.CLAIM]: { visible: true, editable: false },
      [WorkflowUIField.CN]: { visible: true, editable: false },
      [WorkflowUIField.CREDIT_AMOUNT]: { visible: true, editable: false },
      [WorkflowUIField.NOTES]: { visible: true, editable: true }
    }
  )
};

// Helper functions for accessing permissions
export function getFieldPermissions(
  status: InvoiceStatus,
  role: UserRole,
  field: WorkflowUIField
): FieldPermission {
  return WORKFLOW_FIELD_PERMISSIONS[status]?.[role]?.[field] || { visible: false, editable: false };
}

export function getRolePermissions(status: InvoiceStatus, role: UserRole): Record<WorkflowUIField, FieldPermission> {
  return WORKFLOW_FIELD_PERMISSIONS[status]?.[role] || {};
}

export function getStatusPermissions(status: InvoiceStatus): RolePermissions {
  return WORKFLOW_FIELD_PERMISSIONS[status] || {};
}

export function isFieldVisible(status: InvoiceStatus, role: UserRole, field: WorkflowUIField): boolean {
  return getFieldPermissions(status, role, field).visible;
}

export function isFieldEditable(status: InvoiceStatus, role: UserRole, field: WorkflowUIField): boolean {
  return getFieldPermissions(status, role, field).editable;
}

export function getVisibleFields(status: InvoiceStatus, role: UserRole): WorkflowUIField[] {
  const permissions = getRolePermissions(status, role);
  return Object.entries(permissions)
    .filter(([_, permission]) => permission.visible)
    .map(([field, _]) => field as WorkflowUIField);
}

export function getEditableFields(status: InvoiceStatus, role: UserRole): WorkflowUIField[] {
  const permissions = getRolePermissions(status, role);
  return Object.entries(permissions)
    .filter(([_, permission]) => permission.editable)
    .map(([field, _]) => field as WorkflowUIField);
}